import type { BtlApplication } from '@api/btlApplicationForm/types';
import { PropertyTenure } from '@api/btlApplicationForm/types';
import { firmApi } from '@api/firm/firm.api';
import type { ExistingFirmResponse } from '@api/firm/firm.types';
import { isStrideUpInternalFirm } from '@utils/strideUpInternalFirm';
import { monthsToYears } from '@utils/numbers';
import { mapIntroducerFormValuesFromCaseReferrer } from './btlIntroducerHelpers';
import { resolveBtlInitialFunnelTypeFromApi } from './btlSubmissionRouteHelpers';
import { resolveInitialFunnelTypeFromApi } from '@pages/ApplicationFormPage/Submission/submissionRouteHelpers';
import { SubmissionFunnelTypeEnum } from '@pages/ApplicationFormPage/Submission/types';
import { mapDepositSourcesToFormValues } from './depositSourcesHelpers';

/**
 * Owner-occupied AR firms reach a mortgage club through their network. The buy-to-let funnel
 * rules cannot express that, so resolving an owner-occupied case with them treats a saved club
 * selection as unofferable and silently rewrites it to Direct.
 */
function resolveFunnelTypeForApplication(data: BtlApplication, firm: ExistingFirmResponse): SubmissionFunnelTypeEnum {
  const savedFunnelType = data.submissionRoute?.submissionFunnelType;

  return data.applicationType === 'OWNER_OCCUPIED'
    ? resolveInitialFunnelTypeFromApi(firm, savedFunnelType)
    : resolveBtlInitialFunnelTypeFromApi(firm, savedFunnelType);
}

export function mapApplicationToFormValues(data?: BtlApplication, firm?: ExistingFirmResponse | null) {
  if (!data) {
    const submissionFunnelType = firm ? resolveBtlInitialFunnelTypeFromApi(firm, undefined) : undefined;

    return {
      purpose: 'PURCHASE',
      feesAddedToBalance: false,
      ...(submissionFunnelType ? { submissionFunnelType } : {}),
    };
  }

  const submissionFunnelType = firm ? resolveFunnelTypeForApplication(data, firm) : undefined;

  const { applicationProperty, termInMonths, feesAddedToBalance, purpose, depositSources, ...rest } = data;

  if (purpose === 'PURCHASE') {
    rest.refinancePurpose = null;
  }

  const mappedDepositSources = mapDepositSourcesToFormValues(depositSources);

  const { tenure, yearsRemainingOnLease, estateAgent } = applicationProperty ?? {};
  const normalisedTenure: PropertyTenure | null = tenure === 'LEASEHOLD' || tenure === 'FREEHOLD' ? tenure : null;

  const sharedValues = {
    ...rest,
    purpose,
    ...(submissionFunnelType ? { submissionFunnelType } : {}),
    feesAddedToBalance: [feesAddedToBalance],
    estateAgent,
    termInMonths: termInMonths ? monthsToYears(termInMonths) : undefined,
    tenure: normalisedTenure,
    yearsRemainingOnLease,
    depositSources: mappedDepositSources,
  };

  if (applicationProperty?.status === 'REJECTED') {
    return sharedValues;
  }

  const {
    purchasePrice,
    grossMonthlyRentalPayment,
    propertyType,
    propertySubType,
    expectedPropertyType,
    address,
    attributes,
  } = applicationProperty?.property ?? {};
  const { numberOfBedrooms, hasCommunalSpace, numberOfUnits } = attributes ?? {};

  return {
    ...sharedValues,
    purchasePrice,
    grossMonthlyRentalPayment,
    propertyType,
    propertySubType,
    expectedPropertyType,
    address: { ...address, isManual: true },
    numberOfBedrooms,
    hasCommunalSpace: [hasCommunalSpace],
    numberOfUnits,
  };
}

export async function hydrateFinancingAndPropertyFormValues(
  data?: BtlApplication,
  firm?: ExistingFirmResponse | null,
  brokerReferrerId?: string,
): Promise<Record<string, unknown>> {
  const baseValues = mapApplicationToFormValues(data, firm);

  if (!data || !firm || !isStrideUpInternalFirm(firm) || !brokerReferrerId) {
    return baseValues;
  }

  const caseReferrerId = data.submissionRoute?.referrerId;
  let introducerReferrer = null;

  if (caseReferrerId && caseReferrerId !== brokerReferrerId) {
    try {
      introducerReferrer = await firmApi.getIntroducerById(caseReferrerId);
    } catch {
      introducerReferrer = null;
    }
  }

  const introducerValues = mapIntroducerFormValuesFromCaseReferrer(
    caseReferrerId,
    brokerReferrerId,
    introducerReferrer,
  );

  if (!introducerValues) {
    return baseValues;
  }

  return {
    ...baseValues,
    ...introducerValues,
  };
}

export async function hydrateOoFinancingFormValues(
  data?: BtlApplication,
  firm?: ExistingFirmResponse | null,
  brokerReferrerId?: string,
): Promise<Record<string, unknown>> {
  const baseValues = await hydrateFinancingAndPropertyFormValues(data, firm, brokerReferrerId);
  const intermediaryFees = data?.submissionRoute?.intermediaryFees ?? [];
  const submissionFunnelType = firm
    ? resolveInitialFunnelTypeFromApi(firm, data?.submissionRoute?.submissionFunnelType)
    : baseValues.submissionFunnelType;

  return {
    ...baseValues,
    ...(submissionFunnelType ? { submissionFunnelType } : {}),
    hasIntermediaryFees: intermediaryFees.length > 0 ? 'true' : 'false',
    fees: intermediaryFees,
    region: data?.applicationProperty?.property?.region,
  };
}
